import { useTranslation } from "react-i18next";
import type { RegistrationCheck, RegistrationIssue } from "@/lib/registry/validateRegistration";
import { cn } from "@/lib/utils";

/**
 * EL AVISO DEL Nº DE REGISTRO (F4 del plan del cuaderno, 26-09-2026; D2: H1 + H2).
 *
 * Una línea debajo del nº, en el alta, la ficha, crear tarea y el paso Revisar
 * del registro manual. NO BLOQUEA (T4): no deshabilita nada ni corta un
 * guardado; solo dice qué le pasa al nº para que alguien lo corrija antes de
 * que llegue al cuaderno. Qué aviso sale lo decide `validateRegistration`.
 *
 * `ok`, `off` (D26) y `not_applicable` no pintan nada. `no_data` (sin catálogo
 * con que comprobarlo) se dice como «sin dato» solo si quien llama lo pide
 * —mientras el catálogo llega no hay que decir nada—, y nunca como «correcto»
 * (A15).
 */
export function RegistrationCheckNote({
  check,
  hide = [],
  showNoData = false,
  className,
}: {
  check: RegistrationCheck | null | undefined;
  /** Avisos que en ESTA pantalla ya dice otro (p. ej. el «No vigente» de la ficha elegida). */
  hide?: readonly RegistrationIssue[];
  showNoData?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!check) return null;

  if (check.state === "no_data") {
    if (!showNoData) return null;
    return (
      <p
        role="status"
        data-testid="registration-check"
        data-state="no_data"
        className={cn("text-[11px] leading-snug text-muted-foreground", className)}
      >
        {t("inventory.registryCheck.noData", "Nº sin comprobar: sin dato del Registro MAPA")}
      </p>
    );
  }
  if (check.state !== "issue" || !check.issue || hide.includes(check.issue)) return null;

  const raw = check.raw ?? "";
  const text = (() => {
    switch (check.issue) {
      case "missing":
        return t("inventory.registryCheck.missing", "Sin nº de registro");
      case "junk":
        return t("inventory.registryCheck.junk", "«{{raw}}» no es un nº de registro", { raw });
      case "not_in_registry":
        return t("inventory.registryCheck.notInRegistry", "El nº {{raw}} no existe en el Registro MAPA", { raw });
      case "other_product":
        return t("inventory.registryCheck.otherProduct", "El nº {{raw}} es de {{name}} en el Registro MAPA, no de este producto", {
          raw,
          name: check.registryName ?? "",
        });
      case "not_current":
        return t("inventory.registryCheck.notCurrent", "No vigente en el Registro MAPA (cancelado)");
      default:
        return null;
    }
  })();
  if (!text) return null;

  return (
    <p
      role="status"
      data-testid="registration-check"
      data-state="issue"
      data-issue={check.issue}
      className={cn("text-[11px] leading-snug font-semibold text-amber-700", className)}
    >
      {text}
    </p>
  );
}

export default RegistrationCheckNote;
